import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Flex, Text, Box,
  useDisclosure, Spinner, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FilterBar } from '../shared/filters/FilterBar';
import { useFilters } from '../../contexts/FilterContext';
import { useDashboardData, transformGroupedDataToMetrics, buildCategoryMap, buildConvertedCategoryBreakdown } from '../../hooks/useDashboardData';
import { updateMovement, fetchTags, fetchIncomeVsExpenses } from '../../api/endpointsBackend';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import DashboardSummaryCards from './DashboardSummaryCards';
import DashboardSpendingTrend from './DashboardSpendingTrend';
import DashboardIncompleteMovements from './DashboardIncompleteMovements';
import DashboardTopCategories from './DashboardTopCategories';
import DashboardCurrencyTabs from './DashboardCurrencyTabs';
import DashboardGoalsCard from './DashboardGoalsCard';
import DashboardStatementStatus from './DashboardStatementStatus';
import DashboardNetWorth from './DashboardNetWorth';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';
import { mergeRateSources } from '../../utils/rateSources';

import Card from '../shared/Card';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneHome from '../phone/PhoneHome';

// A row's wide and narrow card. Each fills the row's height, and the narrow
// one drops below when the panel cannot fit both side by side.
const STRETCH = { minW: 0, display: 'flex', flexDirection: 'column', sx: { '& > *': { flex: 1 } } };
const WIDE = { ...STRETCH, flex: '7 1 440px' };
const NARROW = { ...STRETCH, flex: '5 1 340px' };

/**
 * The dashboard: Home on the phone, the desktop dashboard otherwise. The
 * branch sits here, above both, so neither one's hooks run for the other.
 */
export default function Dashboard(props) {
  return useIsPhone() ? <PhoneHome {...props} /> : <DashboardDesktop {...props} />;
}

/**
 * Dashboard — orchestrates the dashboard layout, shared state, and data flow.
 * Each card is a separate component in this directory.
 */
function DashboardDesktop() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('dashboard');
  const goalsEnabled = useGoalsEnabled();
  const { can } = useWorkspace();
  const [allTags, setAllTags] = useState([]);
  const [incompletePatched, setIncompletePatched] = useState(null);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [currencyDeltas, setCurrencyDeltas] = useState({});

  const {
    dateRange, selectedFilters, filterGroups, groupsOperator,
    isAdvancedMode, filterOptions, filterOptionsLoading,
    apiFilters, apiFilterGroups, timePresets,
    addFilter, appendFilter, removeFilter, updateFilter, clearFilters,
    applySuggestion, removeLastFilter, fetchSuggestions, applyAiFilter, applyAiPeriod,
    navigatePeriod, setDateMode, goToToday, setCustomRange,
    enableAdvancedMode, disableAdvancedMode,
    addFilterGroup, removeFilterGroup, toggleGroupOperator, toggleGroupsOperator,
    addGroupCondition, removeGroupCondition,
  } = useFilters();

  const { data, loading, error } = useDashboardData(dateRange, apiFilters, apiFilterGroups, groupsOperator);

  // Reset local patched state when fresh data arrives
  useEffect(() => {
    setIncompletePatched(null);
  }, [data.incompleteMovements]);

  // Load tags once for the inline tag picker
  useEffect(() => {
    fetchTags().then(setAllTags).catch(() => {});
  }, []);

  const handleCategoryChange = useCallback(async (movementId, category) => {
    try {
      await updateMovement(movementId, { category_id: category.id });
      const current = incompletePatched || data.incompleteMovements;
      setIncompletePatched(current.filter(m => m.id !== movementId));
      toast({ title: t('toasts.categoryUpdated', { name: category.name }), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('toasts.categoryUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [incompletePatched, data.incompleteMovements, toast, t]);

  const handleTagsChange = useCallback(async (movementId, newTagIds, tags) => {
    try {
      await updateMovement(movementId, { tag_ids: newTagIds });
      const newTagMovements = newTagIds.map(id => {
        const tag = tags.find(tg => tg.id === id);
        return { tag_id: id, movement_id: movementId, tag };
      });
      const current = incompletePatched || data.incompleteMovements;
      setIncompletePatched(current.map(m => m.id === movementId ? { ...m, tag_movements: newTagMovements } : m));
    } catch {
      toast({ title: t('toasts.tagsUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [incompletePatched, data.incompleteMovements, toast, t]);

  const categoryMap = useMemo(() => buildCategoryMap(data.categories), [data.categories]);
  const metrics = useMemo(() => transformGroupedDataToMetrics(data, categoryMap), [data, categoryMap]);

  // MI-298: approximate income/expenses/net converted to the display currency,
  // respecting the same filters + date range as the dashboard. Grouping by
  // ['category_id', 'type'] makes the same request also carry per-category
  // converted totals (`buckets`) for the Top Categories card — one call, both.
  const { data: convertedTotal, loading: convertedLoading, displayCurrency } =
    useConvertedTotal(apiFilters, {
      startDate: dateRange.start_date,
      endDate: dateRange.end_date,
      groups: apiFilterGroups,
      groupsOperator,
      dimensions: ['category_id', 'type'],
    });

  // Collapse the converted buckets into one currency-less category breakdown,
  // used by Top Categories only while a display currency is active.
  const convertedCategoryBreakdown = useMemo(
    () => buildConvertedCategoryBreakdown(convertedTotal?.buckets, categoryMap),
    [convertedTotal, categoryMap],
  );

  // MI-329: how each converted card was converted, from the same per-type
  // buckets: the rates its own movements read, and whether any of them was in
  // another currency at all (a card of pesos shown in pesos is exact).
  const conversions = useMemo(() => {
    const of = (type) => {
      const buckets = (convertedTotal?.buckets || []).filter(b => b.type === type);
      return {
        rateSources: mergeRateSources(...buckets.map(b => b.rate_sources)),
        isConverted: buckets.some(b => b.is_converted),
      };
    };
    const expenses = of('Expenses');
    const income = of('Income');
    return {
      expenses,
      income,
      total: {
        rateSources: mergeRateSources(expenses.rateSources, income.rateSources),
        isConverted: expenses.isConverted || income.isConverted,
      },
    };
  }, [convertedTotal]);

  const availableCurrencies = useMemo(() => {
    const all = new Set([...Object.keys(metrics.expenses), ...Object.keys(metrics.income)]);
    return [...all];
  }, [metrics.expenses, metrics.income]);

  // Currencies for tabs: from filterOptions (always full list, not filtered by active currency)
  const tabCurrencies = useMemo(() => {
    const currencyOption = (filterOptions || []).find(f => f.column === 'currency');
    return currencyOption?.options?.map(o => o.value ?? o.displayName) ?? availableCurrencies;
  }, [filterOptions, availableCurrencies]);

  // Raw currency from active filter (syncs with chip removal)
  const selectedCurrency = useMemo(() => {
    const f = selectedFilters.find(sf => sf.column === 'currency');
    return f?.values?.length === 1 ? f.values[0] : 'all';
  }, [selectedFilters]);

  // Effective currency: auto-select the only currency when just one exists
  const effectiveCurrency = useMemo(() => {
    if (selectedCurrency !== 'all') return selectedCurrency;
    return tabCurrencies.length === 1 ? tabCurrencies[0] : 'all';
  }, [selectedCurrency, tabCurrencies]);

  const incompleteMovements = useMemo(() => {
    const list = incompletePatched || data.incompleteMovements;
    if (effectiveCurrency === 'all') return list;
    return list.filter(m => m.currency === effectiveCurrency);
  }, [incompletePatched, data.incompleteMovements, effectiveCurrency]);

  const handleCurrencyTabChange = useCallback((currency) => {
    addFilter({ entity: 'Movement', column: 'currency', operator: 'in', values: currency === 'all' ? [] : [currency] });
  }, [addFilter]);

  // Income/expenses deltas vs the previous period, per currency shown: every
  // currency on "All", the selected one otherwise. Net Balance reads them.
  const availableCurrenciesKey = availableCurrencies.join(',');
  useEffect(() => {
    const deltaCurrencies = effectiveCurrency === 'all' ? availableCurrencies : [effectiveCurrency];
    if (deltaCurrencies.length === 0) {
      setCurrencyDeltas({});
      return;
    }
    // A slower answer for an earlier period must not overwrite this one's.
    let cancelled = false;
    Promise.all(
      deltaCurrencies.map(cur =>
        fetchIncomeVsExpenses(dateRange?.start_date || null, dateRange?.end_date || null, cur, apiFilters, apiFilterGroups, groupsOperator)
          .then(d => ({
            cur,
            expensesDelta: d.expenses?.delta_pct ?? null,
            incomeDelta: d.income?.delta_pct ?? null,
            previousIncome: d.income?.previous_total ?? null,
            previousExpenses: d.expenses?.previous_total ?? null,
          }))
          .catch(() => ({ cur, expensesDelta: null, incomeDelta: null, previousIncome: null, previousExpenses: null }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => {
        map[r.cur] = {
          expensesDelta: r.expensesDelta,
          incomeDelta: r.incomeDelta,
          previousIncome: r.previousIncome,
          previousExpenses: r.previousExpenses,
        };
      });
      setCurrencyDeltas(map);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCurrency, availableCurrenciesKey, dateRange?.start_date, dateRange?.end_date, JSON.stringify({ apiFilters, apiFilterGroups, groupsOperator })]);

  const handleNavigate = useCallback((path) => {
    navigate(`/${path}`);
  }, [navigate]);

  const handleViewIncomplete = useCallback(() => {
    addFilter({ entity: 'Movement', column: 'needs_review', operator: 'eq', values: [true] });
    if (effectiveCurrency !== 'all') {
      addFilter({ entity: 'Movement', column: 'currency', operator: 'in', values: [effectiveCurrency] });
    }
    navigate('/movements');
  }, [addFilter, navigate, effectiveCurrency]);

  const handleMovementClick = useCallback((movementId) => {
    navigate(`/movements?movement_id=${movementId}`);
  }, [navigate]);

  const handleCategoryClick = useCallback((categoryId, categoryName, currency) => {
    setPendingNavigation({ type: 'category', categoryId, categoryName, currency });
    onConfirmOpen();
  }, [onConfirmOpen]);

  const handleConfirmNavigation = useCallback(() => {
    if (!pendingNavigation) return;
    if (pendingNavigation.type === 'category') {
      addFilter({ entity: 'Movement', column: 'category_id', operator: 'in', values: [pendingNavigation.categoryId] });
      if (pendingNavigation.currency) {
        addFilter({ entity: 'Movement', column: 'currency', operator: 'in', values: [pendingNavigation.currency] });
      }
    }
    onConfirmClose();
    setPendingNavigation(null);
    navigate('/movements');
  }, [pendingNavigation, addFilter, navigate, onConfirmClose]);

  const showStatements = can('source_file.read');
  const showGoals = goalsEnabled && can('goal.read');

  const confirmMessage = useMemo(() => {
    if (!pendingNavigation) return '';
    return pendingNavigation.currency
      ? t('confirm.messageCategoryCurrency', { name: pendingNavigation.categoryName, currency: pendingNavigation.currency })
      : t('confirm.messageCategory', { name: pendingNavigation.categoryName });
  }, [pendingNavigation, t]);

  return (
    // `clip`, not `hidden`: a hidden overflow makes this box a scroll container,
    // and the sticky header would stick to it instead of the content panel.
    <Container maxW="1600px" px={{ base: 3, md: 6 }} overflowX="clip">
      {/* Sticky header, on the panel's own ground */}
      <Box
        bg="redesign.bg.page"
        mx={{ base: -3, md: -6 }}
        px={{ base: 3, md: 6 }}
        position="sticky"
        top={0}
        zIndex={10}
        borderBottom="1px solid"
        borderBottomColor="redesign.border"
      >
        <FilterBar
          dateRange={dateRange}
          onNavigatePeriod={navigatePeriod}
          onModeChange={setDateMode}
          onCustomRange={setCustomRange}
          onToday={goToToday}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          selectedFilters={selectedFilters}
          onAddFilter={addFilter}
          onAppendFilter={appendFilter}
          onApplySuggestion={applySuggestion}
          onRemoveLastFilter={removeLastFilter}
          fetchSuggestions={fetchSuggestions}
          onAiFilter={applyAiFilter}
          onParsePeriod={applyAiPeriod}
          onRemoveFilter={removeFilter}
          onUpdateFilter={updateFilter}
          onClearFilters={clearFilters}
          timePresets={timePresets}
          isAdvancedMode={isAdvancedMode}
          filterGroups={filterGroups}
          groupsOperator={groupsOperator}
          onEnableAdvancedMode={enableAdvancedMode}
          onDisableAdvancedMode={disableAdvancedMode}
          onAddFilterGroup={addFilterGroup}
          onRemoveFilterGroup={removeFilterGroup}
          onToggleGroupOperator={toggleGroupOperator}
          onToggleGroupsOperator={toggleGroupsOperator}
          onAddGroupCondition={addGroupCondition}
          onRemoveGroupCondition={removeGroupCondition}
        >
          {tabCurrencies.length > 1 && (
            <DashboardCurrencyTabs currencies={tabCurrencies} selected={selectedCurrency} onChange={handleCurrencyTabChange} />
          )}
        </FilterBar>
      </Box>

      <Box pt={{ base: 3, md: 4 }}>
        {loading && (
          <Flex justify="center" align="center" py={12}>
            <Spinner size="lg" color="redesign.text.muted" />
          </Flex>
        )}

        {error && (
          <Flex justify="center" align="center" py={12}>
            <Text fontSize="lg" color="redesign.accent.red">{error}</Text>
          </Flex>
        )}

        {!loading && !error && (
          <>
            <DashboardSummaryCards
              expenses={metrics.expenses}
              income={metrics.income}
              net={metrics.net}
              selectedCurrency={effectiveCurrency}
              currencyDeltas={currencyDeltas}
              converted={convertedTotal}
              conversions={conversions}
              convertedLoading={convertedLoading}
              displayCurrency={displayCurrency}
            />

            {/* Rows of a wide card beside a narrow one. A row wraps when the
                panel is too narrow for both, and a card the workspace hides
                leaves its partner the whole row. */}
            <Flex direction="column" gap={4} mb={4}>
            <Flex wrap="wrap" gap={4} align="stretch">
              <Box {...WIDE}>
                <DashboardNetWorth selectedCurrency={effectiveCurrency} dateRange={dateRange} />
              </Box>
              <Box {...NARROW}>
              <DashboardSpendingTrend
                selectedCurrency={effectiveCurrency}
                displayCurrency={displayCurrency}
                dateRange={dateRange}
                apiFilters={apiFilters}
                apiFilterGroups={apiFilterGroups}
                groupsOperator={groupsOperator}
                onNavigate={handleNavigate}
              />
              </Box>
            </Flex>
            <Flex wrap="wrap" gap={4} align="stretch">
              <Box {...WIDE}>
              <DashboardIncompleteMovements
                movements={incompleteMovements}
                categories={data.categories}
                allTags={allTags}
                onCategoryChange={handleCategoryChange}
                onTagsChange={handleTagsChange}
                onViewAll={handleViewIncomplete}
                onMovementClick={handleMovementClick}
              />
              </Box>
              <Box {...NARROW}>
              <DashboardTopCategories
                categoryBreakdown={metrics.categoryBreakdown}
                expenseTotals={metrics.expenses}
                incomeTotals={metrics.income}
                selectedCurrency={effectiveCurrency}
                displayCurrency={displayCurrency}
                convertedBreakdown={convertedCategoryBreakdown}
                convertedExpenseTotal={convertedTotal?.expenses || 0}
                convertedIncomeTotal={convertedTotal?.income || 0}
                convertedUnconvertible={convertedTotal?.unconvertible || []}
                convertedNumUnconvertible={convertedTotal?.num_unconvertible || 0}
                conversion={conversions.total}
                convertedLoading={convertedLoading}
                onCategoryClick={handleCategoryClick}
                onNavigate={handleNavigate}
              />
              </Box>
            </Flex>
            {(showStatements || showGoals) && (
              <Flex wrap="wrap" gap={4} align="stretch">
                {showStatements && <Box {...WIDE}><DashboardStatementStatus /></Box>}
                {showGoals && <Box {...NARROW}><DashboardGoalsCard /></Box>}
              </Flex>
            )}
            </Flex>

            {data.summaryByType.length === 0 && (
              <Card p={12} borderRadius="2xl" textAlign="center">
                <Text fontSize="xl" color="redesign.text" mb={2}>{t('empty.title')}</Text>
                <Text color="redesign.text.muted">{t('empty.description')}</Text>
              </Card>
            )}
          </>
        )}

        <ConfirmationDialog
          isOpen={isConfirmOpen}
          onClose={onConfirmClose}
          onConfirm={handleConfirmNavigation}
          title={t('confirm.title')}
          message={confirmMessage}
          confirmText={t('confirm.confirmText')}
          confirmColorScheme="gray"
          size="xs"
        />
      </Box>
    </Container>
  );
}
